/**
 * Live Event Monitor Page - Real-time event monitoring with auto-refresh
 * Opens in a new tab/window
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { loadEvents, EventSource } from '../lib/api';
import { Event } from '../lib/types';
import { EventCard } from '../components/EventCard';

const SOURCE_STORAGE_KEY = 'liveMonitor.source';
const VALID_SOURCES: EventSource[] = ['partner', 'poller', 'all'];

function readSourceFromStorage(): EventSource {
  const raw = localStorage.getItem(SOURCE_STORAGE_KEY) as EventSource | null;
  return raw && VALID_SOURCES.includes(raw) ? raw : 'partner';
}

const SOURCE_LABELS: Record<EventSource, { label: string; hint: string }> = {
  partner: { label: 'Delivered', hint: 'Events actually forwarded to a partner backend' },
  poller:  { label: 'Poller',    hint: 'Raw events published by nova-poller (metadata only)' },
  all:     { label: 'Both',      hint: 'Delivered + poller events, merged and deduped' },
};

export const LiveEventMonitor: React.FC = () => {
  const { isAuthenticated, initialize } = useAuthStore();
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [unseenCount, setUnseenCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(10); // seconds
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const [newestEventTimestamp, setNewestEventTimestamp] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [initialLimit, setInitialLimit] = useState(500);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [source, setSource] = useState<EventSource>(() => readSourceFromStorage());

  useEffect(() => {
    initialize();
  }, [initialize]);

  const changeSource = useCallback((next: EventSource) => {
    setSource((prev) => {
      if (prev === next) return prev;
      localStorage.setItem(SOURCE_STORAGE_KEY, next);
      setEvents([]);
      setNewestEventTimestamp(null);
      setIsInitialLoad(true);
      setUnseenCount(0);
      return next;
    });
  }, []);

  const playNotificationSound = useCallback(() => {
    if (!soundEnabled || !audioContext) return;

    try {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.error('Failed to play sound:', error);
    }
  }, [soundEnabled, audioContext]);

  const loadEventsData = useCallback(async (forceFullLoad = false) => {
    setIsLoading(true);
    try {
      // If this is initial load or forced, fetch all events up to limit
      // Otherwise, only fetch new events since the last one we have
      const shouldDoFullLoad = forceFullLoad || isInitialLoad || !newestEventTimestamp;
      
      const data = await loadEvents(
        shouldDoFullLoad
          ? { limit: initialLimit, source }
          : { limit: 100, after: newestEventTimestamp!, source }
      );
      
      const fetchedEvents = data.events || [];
      
      if (shouldDoFullLoad) {
        // Full load: replace all events
        setEvents(fetchedEvents);
        setIsInitialLoad(false);
        
        // Track the newest event's actual timestamp for incremental loading
        if (fetchedEvents.length > 0) {
          setNewestEventTimestamp(fetchedEvents[0].created_at);
        }
      } else {
        // Incremental load: prepend new events
        if (fetchedEvents.length > 0) {
          setEvents(prev => {
            // Merge new events, avoiding duplicates
            const existingIds = new Set(prev.map(e => e.timestamp_eventid));
            const newEvents = fetchedEvents.filter(e => !existingIds.has(e.timestamp_eventid));
            return [...newEvents, ...prev];
          });
          // Update newest timestamp
          setNewestEventTimestamp(fetchedEvents[0].created_at);
          
          // Play sound for new events
          if (fetchedEvents.length > 0) {
            playNotificationSound();
          }
        }
      }
      
      setUnseenCount(data.unseen_count || 0);
    } catch (error: any) {
      console.error('Failed to load events:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isInitialLoad, newestEventTimestamp, initialLimit, playNotificationSound, source]);

  const initAudioContext = useCallback(() => {
    if (!audioContext) {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      setAudioContext(ctx);
    }
  }, [audioContext]);

  const handleSoundToggle = () => {
    if (!soundEnabled) {
      initAudioContext();
    }
    setSoundEnabled(!soundEnabled);
  };

  const testSound = () => {
    if (!audioContext) {
      initAudioContext();
      setTimeout(() => playNotificationSound(), 100);
    } else {
      playNotificationSound();
    }
  };

  const toggleEventDetails = (eventId: string) => {
    setExpandedEvents(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(eventId)) {
        newExpanded.delete(eventId);
      } else {
        newExpanded.add(eventId);
      }
      return newExpanded;
    });
  };

  // Initial load + re-load whenever the user switches source
  useEffect(() => {
    if (isAuthenticated) {
      loadEventsData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, source]);

  // Auto-refresh - only fetch new events (incremental)
  useEffect(() => {
    if (!isAuthenticated || refreshInterval === 0) return;

    const interval = setInterval(() => {
      loadEventsData(false); // Incremental load
    }, refreshInterval * 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, refreshInterval]);

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Not Logged In</h2>
          <p className="text-gray-700 mb-6">Please log in to view events.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
        {/* Header */}
        <div className="bg-white rounded-lg border border-gray-200 p-5 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">
              Live Event Monitor
              {unseenCount > 0 && (
                <span className="ml-2 px-2 py-1 bg-red-500 text-white text-sm rounded-full">
                  {unseenCount}
                </span>
              )}
            </h1>
            {isLoading && (
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            )}
            <span className="text-sm text-gray-500">
              ({events.length} events loaded)
            </span>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <span>🔔 Sound</span>
              <input
                type="checkbox"
                checked={soundEnabled}
                onChange={handleSoundToggle}
                className="w-4 h-4"
              />
            </label>

            <button
              onClick={testSound}
              className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              🔊 Test
            </button>

            <div className="flex items-center gap-2">
              <label className="text-sm whitespace-nowrap">Initial:</label>
              <select
                value={initialLimit}
                onChange={(e) => setInitialLimit(Number(e.target.value))}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
                title="How many events to load initially"
              >
                <option value="100">100</option>
                <option value="500">500</option>
                <option value="1000">1000</option>
                <option value="2000">2000</option>
                <option value="5000">5000</option>
              </select>
            </div>

            <button
              onClick={() => loadEventsData(false)}
              disabled={isLoading}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-light disabled:opacity-60"
              title="Fetch only new events"
            >
              {isLoading ? '⏳' : '🔄'} New
            </button>

            <button
              onClick={() => loadEventsData(true)}
              disabled={isLoading}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 disabled:opacity-60"
              title="Reload all events from scratch"
            >
              {isLoading ? '⏳' : '♻️'} Reload All
            </button>

            <label className="flex items-center gap-2 text-sm">
              <span>Auto-refresh:</span>
              <select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                className="px-3 py-1 border border-gray-300 rounded"
              >
                <option value="0">Off</option>
                <option value="2">2 seconds</option>
                <option value="5">5 seconds</option>
                <option value="10">10 seconds</option>
                <option value="60">60 seconds</option>
              </select>
            </label>
          </div>
        </div>

        {/* Source picker — replaces the old "manually edit URL params" essay */}
        <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 flex flex-wrap items-center gap-3">
          <span className="text-sm text-gray-600">
            Showing <strong>{SOURCE_LABELS[source].label.toLowerCase()}</strong> events —{' '}
            <span className="text-gray-500">{SOURCE_LABELS[source].hint}.</span>
          </span>
          <div
            role="tablist"
            aria-label="Event source"
            className="ml-auto inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm"
          >
            {VALID_SOURCES.map((key) => {
              const isActive = key === source;
              return (
                <button
                  key={key}
                  role="tab"
                  aria-selected={isActive}
                  title={SOURCE_LABELS[key].hint}
                  onClick={() => changeSource(key)}
                  className={`px-3 py-1.5 border-r last:border-r-0 border-gray-300 transition-colors
                    ${isActive
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  {SOURCE_LABELS[key].label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Events List */}
        <div className="space-y-3">
          {events.length === 0 && !isLoading && (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
              No events found
            </div>
          )}

          {events.map((event) => (
            <EventCard
              key={event.timestamp_eventid}
              event={event}
              showNewBadge={true}
              isExpanded={expandedEvents.has(event.timestamp_eventid)}
              onToggleDetails={toggleEventDetails}
            />
          ))}
        </div>
    </div>
  );
};

